<script lang="ts">
  // Modal that plots a marker / vital over time. Accepts one or more series
  // (e.g. systolic + diastolic for blood pressure). Range presets set the visible
  // window; clinical mode (labs) adds an Admission dropdown + long windows to match
  // the Trends page. Zooming the time axis autoscales the y-axis (in TrendChart).
  import TrendChart from './TrendChart.svelte';
  import { phr } from '../lib/stores';
  import { parseDate } from '../lib/labTable';

  type P = { t: number; y: number; flag?: string };
  type Series = { label?: string; color: string; refRange?: string; points: P[] };
  type Preset = { id: string; l: string; days: number };
  let { title, unit = '', series, showTable = true, ranges = false, clinical = false, presets: presetsProp, defaultRange, connect = false, padFrac, yMin, yMax, timeAxis = false, onclose }:
    { title: string; unit?: string; series: Series[]; showTable?: boolean; ranges?: boolean; clinical?: boolean; presets?: Preset[]; defaultRange?: string; connect?: boolean; padFrac?: number; yMin?: number; yMax?: number; timeAxis?: boolean; onclose: () => void } = $props();

  let tableOpen = $state(false);   // always start collapsed
  const DAY = 86400000;

  // Full data domain across every series.
  const allT = $derived(series.flatMap((s) => s.points.map((p) => p.t)).filter(Number.isFinite));
  const maxT = $derived(allT.length ? Math.max(...allT) : Date.now());
  const minT = $derived(allT.length ? Math.min(...allT) : maxT - DAY);

  // Presets: clinical (labs) = long windows; else day windows (high-frequency vitals).
  const DAY_PRESETS = [{ id: '3d', l: '3d', days: 3 }, { id: '5d', l: '5d', days: 5 }, { id: '10d', l: '10d', days: 10 }, { id: '15d', l: '15d', days: 15 }, { id: '30d', l: '30d', days: 30 }];
  const CLINICAL_PRESETS = [{ id: '30d', l: '30d', days: 30 }, { id: '90d', l: '90d', days: 90 }, { id: '6mo', l: '6mo', days: 183 }, { id: '1yr', l: '1yr', days: 365 }, { id: '2yr', l: '2yr', days: 730 }];
  const presets = $derived(presetsProp ?? (clinical ? CLINICAL_PRESETS : DAY_PRESETS));
  const showControls = $derived(ranges || clinical || !!presetsProp);

  // Hospital admissions (clinical mode) — same source as the Trends page.
  const admissions = $derived(
    clinical
      ? ($phr.wrappers || []).filter((w) => !w.archived && !!w.date && ['admission', 'ed', 'inpatient'].includes(w.kind as string)).sort((a, b) => (parseDate(a.date) ?? 0) - (parseDate(b.date) ?? 0))
      : [],
  );
  const admLabel = (w: (typeof admissions)[number]) => w.title || (w.kind === 'ed' ? 'ED visit' : 'Admission');
  const md = (s: string) => { const d = parseDate(s); if (d == null) return ''; const dt = new Date(d); return `${dt.getMonth() + 1}/${dt.getDate()}`; };
  const admRange = (w: (typeof admissions)[number]) => `${md(w.date)} – ${w.endDate && w.endDate !== 'present' ? md(w.endDate) : 'ongoing'}`;

  // Selected range. An explicit defaultRange wins; else labs (clinical) default to
  // the last 90d, vitals to 5d, and everything else to All.
  let sel = $state<string>(defaultRange ?? (clinical ? '90d' : ranges ? '5d' : 'all'));
  let admOpen = $state(false);
  const admActive = $derived(sel.startsWith('adm:'));
  function selectAdmission(w: (typeof admissions)[number]) { admOpen = false; sel = 'adm:' + w.id; }

  const win = $derived.by(() => {
    if (sel.startsWith('adm:')) {
      const w = admissions.find((a) => a.id === sel.slice(4));
      if (w) { const s = parseDate(w.date) ?? minT; const e = w.endDate && w.endDate !== 'present' ? (parseDate(w.endDate) ?? maxT) + DAY : maxT; return { t0: Math.max(minT, s), t1: Math.min(maxT, Math.max(e, s + DAY)) }; }
    }
    const p = presets.find((x) => x.id === sel);
    if (p) return { t0: maxT - p.days * DAY, t1: maxT };
    return { t0: minT === maxT ? minT - DAY : minT, t1: maxT };   // All
  });

  // Value table — rows within the current window.
  const rows = $derived.by(() => {
    const ts = new Set<number>();
    for (const s of series) for (const p of s.points) if (Number.isFinite(p.t) && p.t >= win.t0 && p.t <= win.t1) ts.add(p.t);
    const sorted = [...ts].sort((a, b) => b - a);
    const at = (s: Series, t: number) => s.points.find((p) => p.t === t)?.y;
    return sorted.map((t) => ({ t, vals: series.map((s) => at(s, t)) }));
  });
  // Optional breathing room (padFrac, e.g. body-measure plots): extend the visible
  // x-max (right), and pad the y-axis by padFrac on BOTH top and bottom, so markers
  // never sit on the frame. The values table keeps the *unpadded* window — the
  // padding is purely visual.
  const chartT1 = $derived(padFrac ? win.t1 + (win.t1 - win.t0) * padFrac : win.t1);
  const allY = $derived(series.flatMap((s) => s.points.map((p) => p.y)).filter(Number.isFinite));
  const yPad = $derived.by(() => {
    if (!padFrac || !allY.length) return { min: yMin, max: yMax };
    const lo = Math.min(...allY), hi = Math.max(...allY);
    if (lo === hi) { const d = Math.abs(lo) * 0.05 || 1; return { min: lo - d, max: hi + d }; }
    const pad = (hi - lo) * padFrac;
    return { min: lo - pad, max: hi + pad };
  });

  const total = $derived(rows.length);
  const fmtDT = (t: number) => { const d = new Date(t); const hm = d.getHours() || d.getMinutes(); return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}${hm ? ` ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : ''}`; };
  const fmtV = (v: number | undefined) => (v == null ? '—' : Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10);
</script>

<svelte:window
  onkeydown={(e) => { if (e.key === 'Escape') onclose(); }}
  onclick={(e) => { if (admOpen && !(e.target as HTMLElement).closest('.admwrap')) admOpen = false; }} />
<div class="scrim" onclick={onclose} aria-hidden="true"></div>
<div class="modal" role="dialog" aria-modal="true" aria-label={`${title} trend`}>
  <div class="head">
    <div class="ht">
      <strong>{title}</strong>
      {#if unit}<span class="u faint">{unit}</span>{/if}
      <span class="n faint num">· {total} reading{total === 1 ? '' : 's'}</span>
    </div>
    <button class="x" onclick={onclose} aria-label="Close">✕</button>
  </div>

  <div class="body">
    {#if showControls}
      <div class="rng" role="group" aria-label="Time range">
        {#if clinical && admissions.length}
          <div class="admwrap">
            <button class:on={admActive} onclick={() => (admissions.length === 1 ? selectAdmission(admissions[0]) : (admOpen = !admOpen))} aria-haspopup="menu" aria-expanded={admOpen}>
              Admission{#if admissions.length > 1}<span class="caret" class:open={admOpen} aria-hidden="true">▾</span>{/if}
            </button>
            {#if admOpen}
              <div class="admmenu" role="menu">
                {#each admissions as w (w.id)}
                  <button role="menuitem" class:on={sel === 'adm:' + w.id} onclick={() => selectAdmission(w)}>
                    <span class="adm-t">{admLabel(w)}</span><span class="adm-d faint num">{admRange(w)}</span>
                  </button>
                {/each}
              </div>
            {/if}
          </div>
        {/if}
        {#each presets as r (r.id)}<button class:on={sel === r.id} onclick={() => (sel = r.id)}>{r.l}</button>{/each}
        <button class:on={sel === 'all'} onclick={() => (sel = 'all')}>All</button>
      </div>
    {/if}

    <TrendChart {series} {unit} yMin={yPad.min} yMax={yPad.max} {timeAxis} gapMode={connect ? 'never' : 'fixed'} t0={win.t0} t1={chartT1} height={240} interactive yZoomable />

    {#if series.length > 1}
      <div class="legend">
        {#each series as s (s.label)}<span class="lg"><span class="sw" style={`background:${s.color}`}></span>{s.label}</span>{/each}
      </div>
    {/if}

    {#if showTable}
      <button class="vtoggle" onclick={() => (tableOpen = !tableOpen)} aria-expanded={tableOpen}>
        <span class="chev" class:open={tableOpen} aria-hidden="true">›</span>
        {tableOpen ? 'Hide' : 'Show'} values <span class="faint num">({total})</span>
      </button>
      {#if tableOpen}
        <div class="tbl">
          <table>
            <thead><tr><th>Date</th>{#each series as s (s.label)}<th class="r">{s.label || 'Value'}</th>{/each}</tr></thead>
            <tbody>
              {#each rows as r (r.t)}
                <tr><td class="num">{fmtDT(r.t)}</td>{#each r.vals as v}<td class="r num">{fmtV(v)}</td>{/each}</tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .scrim { position: fixed; inset: 0; z-index: 50; background: color-mix(in srgb, var(--bg) 62%, transparent); backdrop-filter: blur(3px); }
  .modal { position: fixed; z-index: 51; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(96vw, 1080px); max-height: 90dvh; display: flex; flex-direction: column; background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius-lg); box-shadow: 0 24px 60px -20px color-mix(in srgb, var(--bg) 92%, black); overflow: hidden; }
  .head { display: flex; align-items: center; justify-content: space-between; padding: var(--space-4); border-bottom: 1px solid var(--line); }
  .ht { display: flex; align-items: baseline; gap: var(--space-2); min-width: 0; }
  .ht strong { font-size: var(--fs-sm); }
  .ht .u, .ht .n { font-size: var(--fs-2xs); }
  .x { display: grid; place-items: center; width: 28px; height: 28px; border-radius: var(--radius-pill); color: var(--text-3); border: 1px solid var(--line); font-size: var(--fs-xs); flex: 0 0 auto; }
  .x:hover { color: var(--text); background: var(--surface-2); }
  .body { overflow-y: auto; padding: var(--space-4); display: flex; flex-direction: column; gap: var(--space-3); overflow-x: clip; }

  .rng { display: inline-flex; flex-wrap: wrap; justify-content: center; align-self: center; align-items: center; row-gap: 2px; max-width: 100%; background: var(--surface-2); border: 1px solid var(--line); border-radius: var(--radius-md); padding: 3px; }
  .rng button { padding: var(--space-1) var(--space-3); border-radius: var(--radius-pill); font-size: var(--fs-2xs); font-weight: 700; letter-spacing: 0.04em; color: var(--text-3); }
  .rng button:hover { color: var(--text); }
  .rng button.on { color: var(--accent); background: var(--accent-soft); }
  /* Admissions dropdown (clinical mode) */
  .admwrap { position: relative; display: inline-flex; }
  .admwrap > button { display: inline-flex; align-items: center; gap: 3px; }
  .caret { font-size: 9px; transition: transform var(--ease); }
  .caret.open { transform: rotate(180deg); }
  .admmenu { position: absolute; top: calc(100% + 6px); left: 0; z-index: 20; min-width: 220px; display: flex; flex-direction: column; padding: var(--space-1); background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius-md); box-shadow: 0 18px 40px -18px color-mix(in srgb, var(--bg) 92%, black); }
  .admmenu button { display: flex; align-items: center; gap: var(--space-2); width: 100%; text-align: left; padding: var(--space-2); border-radius: var(--radius-sm); color: var(--text-2); }
  .admmenu button:hover { background: var(--surface-2); color: var(--text); }
  .admmenu button.on { background: var(--accent-soft); color: var(--accent); }
  .adm-t { flex: 1; font-size: var(--fs-xs); font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .adm-d { flex: 0 0 auto; font-size: var(--fs-2xs); }

  .legend { display: flex; gap: var(--space-4); justify-content: center; }
  .lg { display: inline-flex; align-items: center; gap: var(--space-2); font-size: var(--fs-xs); color: var(--text-2); }
  .sw { width: 10px; height: 10px; border-radius: 3px; }

  .vtoggle { display: inline-flex; align-items: center; gap: var(--space-2); align-self: flex-start; font-size: var(--fs-xs); font-weight: 700; color: var(--text-2); }
  .vtoggle:hover { color: var(--text); }
  .chev { color: var(--text-3); font-size: var(--fs-md); line-height: 1; transition: transform var(--ease); }
  .chev.open { transform: rotate(90deg); }

  .tbl { border: 1px solid var(--line); border-radius: var(--radius-md); overflow-y: auto; max-height: 34dvh; }
  table { width: 100%; border-collapse: collapse; font-size: var(--fs-xs); }
  thead th { position: sticky; top: 0; background: var(--surface-2); color: var(--text-3); font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; font-size: var(--fs-2xs); padding: var(--space-2) var(--space-3); text-align: left; }
  thead th.r, td.r { text-align: right; }
  tbody td { padding: var(--space-2) var(--space-3); border-top: 1px solid var(--line); color: var(--text-2); }
</style>
